import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../hooks/useNotifications";

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface NotificationsDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export function NotificationsDropdown({
  isOpen,
  onToggle,
  onClose,
}: NotificationsDropdownProps) {
  const { notifications, unreadCount, isLoading, markRead, markAllRead } =
    useNotifications();
  const navigate = useNavigate();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
        aria-expanded={isOpen}
        className={`relative flex h-10 w-10 items-center justify-center rounded-xl transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${isOpen ? "bg-teal-50 text-teal-600" : "text-gray-400 hover:bg-teal-50 hover:text-teal-600"}`}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 border-2 border-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[70]" onClick={onClose} />
          <div className="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-[80] animate-fade-in origin-top-right overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <>
                    <span className="text-[10px] bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full font-bold">
                      {unreadCount} New
                    </span>
                    <button
                      onClick={markAllRead}
                      className="text-[10px] text-gray-400 hover:text-teal-600 transition-colors"
                      title="Mark all as read"
                    >
                      <CheckCheck className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="max-h-[320px] overflow-y-auto">
              {isLoading ? (
                <div className="px-5 py-8 text-center text-sm text-gray-400">
                  Loading...
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No notifications yet</p>
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => {
                      if (!n.read) markRead(n.id);
                    }}
                    className={`px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50 last:border-0 ${!n.read ? "bg-teal-50/30" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                          n.type === "warning"
                            ? "bg-amber-400"
                            : n.type === "success"
                              ? "bg-emerald-400"
                              : n.type === "error"
                                ? "bg-red-400"
                                : "bg-blue-400"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm leading-tight ${!n.read ? "font-semibold text-gray-900" : "font-medium text-gray-600"}`}
                        >
                          {n.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 leading-snug truncate">
                          {n.message}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-2 font-medium">
                          {timeAgo(n.createdAt)}
                        </p>
                      </div>
                      {!n.read && (
                        <div className="w-2 h-2 bg-teal-500 rounded-full mt-1.5 shrink-0" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="p-3 bg-gray-50 border-t border-gray-100 text-center">
                <button
                  onClick={() => {
                    onClose();
                    navigate("/notifications");
                  }}
                  className="text-xs font-bold text-teal-600 hover:text-teal-700 transition-colors"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
